/**
 * AcceptSuggestionModal — the evidence panel for accepting Guardian's triage
 * suggestion across a batch of rows (Drift Triage Copilot, blueprint §5.4 /
 * decision D-7).
 *
 * The copilot never disposes a row on its own. When a human chooses to accept
 * Guardian's shared read for the selected rows, this modal shows the evidence
 * behind that read BEFORE anything is written:
 *   - how many rows, and the read itself (the suggestion copy),
 *   - the set of affected settings across the selection,
 *   - up to five sample rows (resource + setting, with the change for scan rows),
 *   - an optional "review every row" expander (NOT forced per-row review — D-7),
 *   - the shared reason Guardian gave, prefilled and EDITABLE (the human owns
 *     the final wording that lands in the audit trail).
 *
 * Built on the Carbon modal (components/carbon/Modal). Advisor voice, no
 * internal jargon, no em-dashes.
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import type { UnifiedDriftRow } from '@/types/unifiedDrift'
import type { TriageSuggestionCopy } from '@/lib/drift-decision-copy'
import { prettifyResourceType } from '@/lib/tcmResourceNames'
import { Button } from '@/components/carbon/Button'
import { Modal } from '@/components/carbon/Modal'
import { RowDetailSection } from '@/components/carbon/RowDetail'
import { Tag } from '@/components/carbon/Tag'
import { TextArea } from '@/components/carbon/TextArea'

const SAMPLE_LIMIT = 5
const SETTINGS_LIMIT = 12
const MAX_REASON = 500

interface AcceptSuggestionModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (comment: string) => void
  /** The selected rows sharing one suggestion. Drives the evidence panel. */
  rows: UnifiedDriftRow[]
  /** Composed copy for the shared suggestion (pill + provenance). */
  suggestionCopy: TriageSuggestionCopy
  /** Guardian's shared reason, prefilled into the (editable) comment field. */
  prefilledReason: string
  isProcessing: boolean
}

function rowResourceLabel(row: UnifiedDriftRow): string {
  return row.resourceName?.trim() || prettifyResourceType(row.resourceType)
}

export default function AcceptSuggestionModal({
  isOpen,
  onClose,
  onConfirm,
  rows,
  suggestionCopy,
  prefilledReason,
  isProcessing,
}: AcceptSuggestionModalProps) {
  const [comment, setComment] = useState(prefilledReason)
  const [showAllRows, setShowAllRows] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  // Reset the editable reason to Guardian's shared read each time the modal
  // opens (the human then edits from there). Mirrors BulkActionCommentModal.
  useEffect(() => {
    if (isOpen) {
      setComment(prefilledReason)
      setShowAllRows(false)
    }
  }, [isOpen, prefilledReason])

  // Distinct affected settings across the selection — the evidence that the
  // rows really are the same kind of change. TCM summary rows carry a property
  // COUNT rather than a name, so their `propertyPath` reads as "N properties";
  // that's honest and still useful in the set.
  const affectedSettings = useMemo(() => {
    const set = new Set<string>()
    for (const row of rows) {
      if (row.propertyPath) set.add(row.propertyPath)
    }
    return Array.from(set)
  }, [rows])

  const sampleRows = useMemo(
    () => (showAllRows ? rows : rows.slice(0, SAMPLE_LIMIT)),
    [rows, showAllRows],
  )

  const handleConfirm = () => onConfirm(comment.trim())

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleConfirm()
    }
  }

  const count = rows.length
  const rowWord = count === 1 ? 'change' : 'changes'
  const settingWord = affectedSettings.length === 1 ? 'setting' : 'settings'

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="md"
      title={`Accept Guardian's suggestion for ${count} ${rowWord}`}
      subtitle={[`${suggestionCopy.pill}.`, suggestionCopy.provenance].filter(Boolean).join(' ')}
      initialFocus={textareaRef}
      primaryAction={{
        label: isProcessing ? 'Applying' : `Accept for ${count} ${rowWord}`,
        onClick: handleConfirm,
        loading: isProcessing,
      }}
      secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: isProcessing }}
    >
      <div className="flex flex-col gap-6">
        <RowDetailSection title="What this touches">
          <p className="m-0">
            {count} {rowWord} across{' '}
            <b>
              {affectedSettings.length} {settingWord}
            </b>
            .
          </p>
          {affectedSettings.length > 0 ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {affectedSettings.slice(0, SETTINGS_LIMIT).map(setting => (
                <Tag key={setting} kind="outline" title={setting} className="max-w-[240px]">
                  {setting}
                </Tag>
              ))}
              {affectedSettings.length > SETTINGS_LIMIT ? (
                <span className="text-helper text-text-helper">
                  +{affectedSettings.length - SETTINGS_LIMIT} more
                </span>
              ) : null}
            </div>
          ) : null}
        </RowDetailSection>

        <RowDetailSection
          title={showAllRows ? 'Every row' : `Sample rows${count > SAMPLE_LIMIT ? `, first ${SAMPLE_LIMIT}` : ''}`}
        >
          <ul className="m-0 list-none border-t border-line-subtle p-0">
            {sampleRows.map(row => {
              const dsc = row.source === 'DSC'
                ? (row.raw as { oldValue?: string; newValue?: string })
                : null
              return (
                <li key={`${row.source}:${row.id}`} className="border-b border-line-subtle py-3">
                  <p className="m-0 font-semibold text-text-primary">{rowResourceLabel(row)}</p>
                  <p className="m-0 mt-0.5 font-mono text-helper text-text-helper [overflow-wrap:anywhere]">
                    {row.propertyPath ?? 'Whole resource'}
                  </p>
                  {dsc && (dsc.oldValue || dsc.newValue) ? (
                    <p className="m-0 mt-1 font-mono text-helper text-text-secondary [overflow-wrap:anywhere]">
                      {dsc.oldValue || '—'} to {dsc.newValue || '—'}
                    </p>
                  ) : null}
                </li>
              )
            })}
          </ul>
          {count > SAMPLE_LIMIT ? (
            <Button kind="ghost" size="sm" className="-ml-3 mt-2" onClick={() => setShowAllRows(v => !v)}>
              {showAllRows ? 'Show fewer' : `Review every row (${count})`}
            </Button>
          ) : null}
        </RowDetailSection>

        <TextArea
          id="accept-suggestion-reason"
          ref={textareaRef}
          label="Reason, recorded in the audit trail"
          helper={`Guardian prefilled this. Edit it before you accept. ${comment.length}/${MAX_REASON}`}
          value={comment}
          onChange={e => setComment(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isProcessing}
          rows={3}
          maxLength={MAX_REASON}
          placeholder="Why is accepting this the right call?"
        />
      </div>
    </Modal>
  )
}
